import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, waLink } from "@/config/site";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-500 ${
          scrolled
            ? "border-black/[0.06] bg-white/85 shadow-[0_8px_30px_-18px_rgba(28,29,31,0.25)] backdrop-blur-xl"
            : "border-transparent bg-white/60 backdrop-blur-md"
        }`}
      >
        <nav className="container-x flex h-[72px] items-center justify-between" aria-label="Navigazione principale">
          <a href="#home" data-testid="nav-logo" className="flex items-center py-2" aria-label="Farma Store — torna all'inizio">
            <img src="/logo.webp" alt="Farma Store — Parafarmacia e Servizi" className="h-8 w-auto sm:h-9" width="2000" height="343" />
          </a>

          <ul className="hidden items-center gap-7 lg:flex">
            {NAV_LINKS.map((l) => (
              <li key={l.id}>
                <a
                  href={l.href}
                  data-testid={`nav-link-${l.id}`}
                  className="text-sm font-medium text-[#575a61] transition-colors duration-300 hover:text-[#0050e0]"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-button"
              className="btn hidden bg-[#0050e0] px-5 py-2.5 text-sm text-white hover:-translate-y-0.5 hover:bg-[#003fa8] sm:inline-flex"
            >
              <WhatsAppIcon className="h-4 w-4" />
              Scrivici su WhatsApp
            </a>
            <button
              type="button"
              data-testid="nav-menu-button"
              onClick={() => setOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white/70 lg:hidden"
              aria-label="Apri il menu"
              aria-expanded={open}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] flex flex-col bg-[#faf8f5]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="container-x flex h-[72px] items-center justify-between">
              <img src="/logo.webp" alt="Farma Store" className="h-8 w-auto" width="2000" height="343" />
              <button
                type="button"
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white"
                aria-label="Chiudi il menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="container-x flex flex-1 flex-col justify-center gap-2" aria-label="Menu mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => setOpen(false)}
                  className="font-display border-b border-black/[0.06] py-4 text-3xl font-bold tracking-tight text-[#1c1d1f] transition-colors hover:text-[#0050e0]"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <motion.div
              className="container-x pb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp-button"
                className="btn-wa w-full"
                onClick={() => setOpen(false)}
              >
                <WhatsAppIcon className="h-5 w-5 text-[#1faf57]" />
                Scrivici su WhatsApp
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
